/**
 * Bridges onboarding business goals + detected platforms into
 * CreateExperiment measurement goals and variant-setup tips.
 */

import type { OnboardingGoalId } from "@/lib/types/onboarding-goal"
import { ONBOARDING_GOAL_LABELS } from "@/lib/types/onboarding-goal"
import type { PlatformId } from "@/lib/types/platform"
import { getPlatformGuide } from "@/data/platform-guides"

/** Measurement goals used by CreateExperiment / API. */
export type ExperimentGoalId =
  | "form_submissions"
  | "click_through_rate"
  | "page_funnel"
  | "external_page_funnel"
  | "revenue"
  | "session_time"
  | "bounce_rate"

export const EXPERIMENT_GOAL_LABELS: Record<ExperimentGoalId, string> = {
  form_submissions: "Form submissions",
  click_through_rate: "Any click on the page",
  page_funnel: "Destination page",
  external_page_funnel: "Click to another site",
  revenue: "Payments (Stripe)",
  session_time: "Visit time",
  bounce_rate: "Bounce rate",
}

/** Ranked experiment goals for each onboarding outcome (first = default). */
const ONBOARDING_TO_EXPERIMENT_GOALS: Record<
  OnboardingGoalId,
  ExperimentGoalId[]
> = {
  saas: ["form_submissions", "click_through_rate", "page_funnel"],
  ecommerce: ["revenue", "click_through_rate", "page_funnel"],
  newsletter: ["form_submissions", "click_through_rate"],
  agency: ["click_through_rate", "page_funnel", "form_submissions"],
  express: ["form_submissions", "click_through_rate"],
}

/**
 * Ranked measurement goals by site builder — used when onboarding use case
 * is missing, and merged after use-case ranking when both exist.
 */
const PLATFORM_TO_EXPERIMENT_GOALS: Partial<
  Record<PlatformId, ExperimentGoalId[]>
> = {
  // Ecommerce — revenue is the business, CTR on product/collection next.
  shopify: ["revenue", "click_through_rate", "page_funnel"],
  // Newsletter / creator — the business is subscriber revenue, then growing
  // the list (subscribe = form), then the CTA that drives both.
  beehiiv: ["revenue", "form_submissions", "click_through_rate"],
  kit: ["revenue", "form_submissions", "click_through_rate"],
  // Courses / memberships — sales first, then sign-ups on the sales page.
  kajabi: ["revenue", "form_submissions", "page_funnel"],
  podia: ["revenue", "form_submissions", "page_funnel"],
  // Publishing w/ paid memberships — grow subscribers, then CTR, then revenue.
  ghost: ["form_submissions", "click_through_rate", "revenue"],
  // Lead-gen marketing sites — capture leads (forms), then CTA clicks.
  wordpress: ["form_submissions", "click_through_rate", "page_funnel"],
  webflow: ["form_submissions", "click_through_rate", "page_funnel"],
  wix: ["form_submissions", "click_through_rate", "page_funnel"],
  squarespace: ["form_submissions", "click_through_rate", "page_funnel"],
  // Landing-page builders — conversion via CTA clicks leads.
  framer: ["click_through_rate", "form_submissions", "page_funnel"],
  astro: ["click_through_rate", "form_submissions", "page_funnel"],
  // App frameworks / builders — signups (forms) then CTA clicks.
  nextjs: ["form_submissions", "click_through_rate", "page_funnel"],
  bubble: ["form_submissions", "click_through_rate", "page_funnel"],
  lovable: ["form_submissions", "click_through_rate", "page_funnel"],
  reactrouter: ["form_submissions", "click_through_rate", "page_funnel"],
  replit: ["form_submissions", "click_through_rate", "page_funnel"],
  bolt: ["form_submissions", "click_through_rate", "page_funnel"],
  v0: ["form_submissions", "click_through_rate", "page_funnel"],
  vuejs: ["form_submissions", "click_through_rate"],
  django: ["form_submissions", "page_funnel", "click_through_rate"],
  laravel: ["form_submissions", "page_funnel", "click_through_rate"],
}

type PlatformVariantTip = {
  headline: string
  steps: string[]
}

/**
 * NOTE: these publish/deploy tips apply to **page-level** tests only — both
 * variant URLs must exist on the live site. No-Code element tests need no
 * deploy: the tracking script overrides text/CSS/images at runtime.
 */

const PLATFORM_VARIANT_TIPS: Partial<Record<PlatformId, PlatformVariantTip>> = {
  webflow: {
    headline: "Publish both variant pages before launching",
    steps: [
      "Create the variant page in the Designer",
      "Publish the site to the domain you’re testing",
      "Both URLs must load on the live domain. Designer preview won’t serve",
    ],
  },
  framer: {
    headline: "Publish the Framer project so both page URLs are live",
    steps: [
      "Create the variant page in the project",
      "Hit Publish. Draft pages won’t serve",
      "Verify both URLs load on the live domain",
    ],
  },
  shopify: {
    headline: "Both pages must be on the published theme",
    steps: [
      "Create the variant page / template on the live theme",
      "Confirm both URLs load on the live storefront",
      "Theme previews won’t serve the test",
    ],
  },
  wordpress: {
    headline: "Publish both pages and clear cache",
    steps: [
      "Publish the variant page (not a draft)",
      "Purge WP Rocket, LiteSpeed, or host cache",
      "Verify both URLs in a private window on the live domain",
    ],
  },
  bubble: {
    headline: "Deploy to live: /version-test won’t run the experiment",
    steps: [
      "Create the variant page in the Bubble editor",
      "Deploy to live (not test)",
      "Verify both URLs on the live app domain",
    ],
  },
  beehiiv: {
    headline: "Both pages must be on the public Beehiiv site URL",
    steps: [
      "Publish the variant page in Beehiiv",
      "Use public website URLs in the variant fields, not admin",
      "Verify both load in a private window",
    ],
  },
  kit: {
    headline: "Publish both landing pages you’re testing",
    steps: [
      "Publish the variant landing page in Kit",
      "Use the public Kit page URLs",
      "Verify both load on the live domain",
    ],
  },
  wix: {
    headline: "Publish the site so both page URLs are live",
    steps: [
      "Create the variant page in the Wix editor",
      "Publish to the custom domain",
      "Verify both URLs on the published site",
    ],
  },
  squarespace: {
    headline: "Both pages must be published (not drafts)",
    steps: [
      "Create the variant page in Squarespace",
      "Set it to Published, not Draft or password-protected",
      "Verify both URLs on the public domain",
    ],
  },
  nextjs: {
    headline: "Deploy production. Both routes must be live",
    steps: [
      "Add the variant route / page in your app",
      "Deploy to production",
      "Verify both URLs on the deployed domain. Local / preview won’t serve",
    ],
  },
  lovable: {
    headline: "Publish / deploy so both page URLs are live",
    steps: [
      "Create the variant page in Lovable",
      "Publish so it hits the live URL",
      "Verify both URLs on production, not the editor preview",
    ],
  },
  ghost: {
    headline: "Publish both pages before launch",
    steps: [
      "Publish the variant page on the live Ghost site",
      "Clear CDN cache if you use one",
      "Verify both URLs on the public domain",
    ],
  },
  kajabi: {
    headline: "Both pages must be live on the public Kajabi URL",
    steps: [
      "Publish the variant landing / sales page",
      "Use the public URLs in the variant fields",
      "Verify both load when launching",
    ],
  },
  podia: {
    headline: "Publish both storefront pages you’re testing",
    steps: [
      "Publish the variant page in Podia",
      "Use your public Podia / custom domain URLs",
      "Verify both load before launch",
    ],
  },
}

export function suggestedExperimentGoals(
  goal: OnboardingGoalId | null | undefined,
  platform?: PlatformId | null,
): ExperimentGoalId[] {
  const fromGoal = goal ? ONBOARDING_TO_EXPERIMENT_GOALS[goal] : null
  const fromPlatform =
    platform && platform !== "generic"
      ? PLATFORM_TO_EXPERIMENT_GOALS[platform]
      : null

  // A detected platform is the strongest signal for the business model
  // (a Beehiiv site is a newsletter, whatever the user picked at onboarding),
  // so it leads the ranking; the onboarding goal fills in any gaps after.
  if (fromPlatform?.length && fromGoal?.length) {
    const merged = [...fromPlatform]
    for (const id of fromGoal) {
      if (!merged.includes(id)) merged.push(id)
    }
    return merged
  }

  return (
    fromPlatform ??
    fromGoal ?? ["form_submissions", "click_through_rate"]
  )
}

export function defaultExperimentGoal(
  goal: OnboardingGoalId | null | undefined,
  platform?: PlatformId | null,
): ExperimentGoalId {
  return suggestedExperimentGoals(goal, platform)[0]
}

/**
 * Preferred variant method for a builder.
 * No-Code element tests are recommended everywhere — the tracking script
 * applies text/CSS/image overrides at runtime, so no deploy or republish is
 * needed on any platform (including app frameworks like Next.js).
 * Page-level tests are the alternative when comparing two live URLs.
 */
export function preferredTestType(
  _platform?: PlatformId | null,
): "element" | "page" {
  return "element"
}

/**
 * One-line, platform-native framing for the Setup step. Names the platform and
 * leads with the outcome its business model cares about, in that platform's own
 * vocabulary. Copy stays neutral (no color words) — it feeds a muted hint line.
 */
function getPlatformExperimentHint(
  platform: PlatformId | null | undefined,
): string | null {
  if (!platform || platform === "generic") return null
  const name = getPlatformLabel(platform) ?? "your site"
  switch (platform) {
    case "shopify":
      return `On ${name}, lift store revenue. Test your product & checkout CTAs.`
    case "beehiiv":
    case "kit":
      return `On ${name}, grow subscriber revenue. Test your subscribe & upgrade CTA.`
    case "kajabi":
    case "podia":
      return `On ${name}, convert more course & membership sales. Test your sales page.`
    case "ghost":
      return `On ${name}, grow subscribers. Test your subscribe CTA on posts & landing pages.`
    case "webflow":
    case "wix":
    case "squarespace":
    case "wordpress":
      return `On ${name}, capture more leads. Test your form & CTA copy.`
    case "framer":
    case "astro":
      return `On ${name}, lift landing-page conversion. Test your hero & CTA.`
    case "nextjs":
    case "bubble":
    case "lovable":
    case "reactrouter":
    case "replit":
    case "bolt":
    case "v0":
    case "vuejs":
    case "django":
    case "laravel":
      return `On ${name}, drive more signups. Test your CTA & form (applied at runtime, no redeploy).`
    default:
      return null
  }
}

/** Publish/deploy steps for page-level tests. Not used for element tests. */
export function getPlatformVariantTip(
  platform: PlatformId | null | undefined,
): PlatformVariantTip | null {
  if (!platform || platform === "generic") return null
  return PLATFORM_VARIANT_TIPS[platform] ?? null
}

export function getOnboardingGoalLabel(goal: OnboardingGoalId | null | undefined): string | null {
  if (!goal) return null
  return ONBOARDING_GOAL_LABELS[goal] ?? null
}

/** One-line use-case hint for CreateExperiment Setup. */
function getUseCaseExperimentHint(
  goal: OnboardingGoalId | null | undefined,
): string | null {
  switch (goal) {
    case "saas":
      return "For trial & signup flows, start with Form Submissions or CTR on pricing."
    case "ecommerce":
      return "For store revenue, start with Revenue or CTR on product / collection pages."
    case "newsletter":
      return "For list growth, measure Form Submissions on your subscribe page."
    case "agency":
      return "For client landing pages, start with CTR or Destination Page."
    case "express":
      return "Popular starting points: Form Submissions or Click-through Rate."
    default:
      return null
  }
}

/**
 * Prefer the detected platform's framing (most specific, speaks its own
 * vocabulary); fall back to the onboarding use-case hint when no platform.
 */
export function getExperimentSetupHint(
  goal: OnboardingGoalId | null | undefined,
  platform?: PlatformId | null,
): string | null {
  return getPlatformExperimentHint(platform) ?? getUseCaseExperimentHint(goal)
}

export function getPlatformLabel(platform: PlatformId | null | undefined): string | null {
  if (!platform || platform === "generic") return null
  return getPlatformGuide(platform).name
}

/** Path fragment heuristics per business goal for suggesting a page to test. */
const GOAL_PATH_HINTS: Record<OnboardingGoalId, RegExp[]> = {
  saas: [
    /sign[-_]?up|register|trial|pricing|plans|demo|get[-_]?started|start|checkout/i,
  ],
  ecommerce: [
    /product|collection|shop|cart|checkout|store|buy/i,
  ],
  newsletter: [
    /subscribe|newsletter|join|signup|sign[-_]?up|list/i,
  ],
  agency: [
    /pricing|case[-_]?stud|work|portfolio|contact|book|demo/i,
  ],
  express: [/pricing|signup|sign[-_]?up|contact/i],
}

type PageLike = {
  url?: string
  page?: string
  views?: number
  pageviews?: number
  count?: number
}

function pagePath(p: PageLike): string {
  const raw = (p.url || p.page || "").trim()
  if (!raw) return ""
  try {
    if (raw.startsWith("http")) {
      return new URL(raw).pathname || "/"
    }
  } catch {
    /* ignore */
  }
  return raw.startsWith("/") ? raw : `/${raw}`
}

function pageScore(p: PageLike): number {
  return p.views ?? p.pageviews ?? p.count ?? 0
}

/**
 * Suggest a page URL to test from the property's pages list,
 * biased by onboarding goal path hints, then highest traffic non-home.
 */
export function suggestPageForExperiment(
  pages: PageLike[],
  goal: OnboardingGoalId | null | undefined,
  domain?: string,
): string | null {
  if (!pages?.length) {
    return domain ? `https://${domain.replace(/^https?:\/\//, "").replace(/\/$/, "")}/` : null
  }

  const ranked = [...pages]
    .map((p) => ({ p, path: pagePath(p), score: pageScore(p) }))
    .filter((x) => x.path)
    .sort((a, b) => b.score - a.score)

  if (!ranked.length) return null

  const hints = goal ? GOAL_PATH_HINTS[goal] : null
  if (hints) {
    const match = ranked.find(({ path }) =>
      hints.some((re) => re.test(path)) && path !== "/",
    )
    if (match) return match.p.url || match.p.page || match.path
  }

  const nonHome = ranked.find(({ path }) => path !== "/" && path !== "")
  const pick = nonHome ?? ranked[0]
  return pick.p.url || pick.p.page || pick.path
}
